// ─────────────────────────────────────────────────────────────────────────────
// transportLane.ts — WHAT THE TRANSPORT CHIP MAY SAY, AND WHETHER IT MAY MOVE.
//
// Pure. No React, no fetch, no provider, no roster. It takes the transport state
// the route returned and returns what the surface may render — pure for the same
// reason `callTarget.ts` is: the interesting cases here are STATES, not renders,
// and a state machine reachable only through a component is a state machine that
// gets exercised in three of its six states.
//
// ── THE LABELS ARE RULED, NOT CHOSEN ───────────────────────────────────────
// `LOCAL TTS` and `PHONE NUMBER`. Both are owner-ruled strings. The carrier
// behind the phone lane is NEVER named — not here, not in the UI, not in an
// identifier. This module knows two lanes and no vendors.
//
// ── WHY THERE IS A SEPARATE ANSWER FOR "NO ROUTE" AND "UNREAD ROUTE" ───────
// Because they are different sentences, and the whole value of this control is
// that it says which one is true. `PHONE NUMBER NOT PUBLISHED` is a statement
// about the system: nothing declares a phone lane yet. `TRANSPORT STATE UNREAD`
// is a statement about this read: something answered and we could not use it.
// Rendering both as one grey chip would be a green that means nothing, which is
// the recurring failure this surface keeps paying for.
//
// ── TRANSPORT IS READ AT THE CALL SITE, NEVER ON THE BINDING ───────────────
// OWNER RULING 2026-08-29, confirming the standing compartmentalisation rule.
// The per-agent setting is read HERE, by the button that places the call. It is
// never read when the daemon BINDS an agent. If it were, arming TRADING:arthur
// would silently change the CornerOrb too — the orb takes `{activeDomain,
// activeAgent}` off the homepage, the same pair this button uses — and the two
// lanes would mesh, which is the thing that has been forbidden three times.
//
// THE VISIBLE CONSEQUENCE, WHICH IS CORRECT AND MUST NOT LOOK LIKE A BUG: on
// Arthur's page with the phone lane armed, the CALL button places a phone call
// and the orb places a local one. Two controls, one agent, two transports. The
// armed sentence below says so out loud rather than leaving it to be discovered.
//
// ── AND WHY THE CHIP STILL SAYS `LOCAL TTS` WHILE UNPUBLISHED ──────────────
// That is not a guess about capability and it is not a default dressed as a
// choice. It is this client stating ITS OWN WIRING, which it can read with
// certainty: `ComposerAgentControls.pressCall` calls `live.start`, which is
// `useVoiceDaemonPTT.start`, which reaches the local daemon. There is exactly one
// branch in that path and it goes to the local pipeline. So "a call from this
// chat is carried by LOCAL TTS" is measured, not assumed — while "the phone lane
// exists" is unmeasurable from here and is therefore never claimed.
// ─────────────────────────────────────────────────────────────────────────────

import {
  AgentTransportState, TransportLane, LOCAL_TTS, PHONE_NUMBER,
} from '../api/agentTransport';

/** The ruled labels. The only two strings this control ever puts on a chip. */
export const LANE_LABEL: { readonly local_tts: string; readonly phone_number: string } = {
  local_tts: 'LOCAL TTS',
  phone_number: 'PHONE NUMBER',
};

/** Own-property lookup by lane. The lane values are ours, not the board's, but
 *  the habit is cheap and the alternative has already reached a render once. */
export function laneLabel(lane: TransportLane): string {
  return lane === PHONE_NUMBER ? LANE_LABEL.phone_number : LANE_LABEL.local_tts;
}

export interface TransportVerdict {
  /** What the chip reads. Always one of the two ruled labels. */
  label: string;
  /**
   * A qualification for the chip's TOOLTIP that is not worth a counted notice.
   *
   * `detail` is a sentence the surface must SHOW; `hint` is one it must have
   * READY. The difference is whether a claim is being made on screen: a chip
   * resting on LOCAL TTS asserts nothing about the phone lane, so its limit is
   * owed to whoever reaches for it, not to everyone who opens the chat.
   */
  hint?: string | null;
  /** The lane a call from this chat would actually take right now. */
  lane: TransportLane;
  /** May the chip be pressed at all. */
  armable: boolean;
  /** The lane pressing it would select. `null` when nothing may be pressed. */
  next: TransportLane | null;
  /** True only when the phone lane is BOTH published and selected — the one
   *  state in which this surface claims a call leaves by the number. */
  onPhone: boolean;
  /** The full sentence, unshortened, for the refusal surface above the row.
   *  `null` when there is nothing to refuse. */
  detail: string | null;
}

/**
 * The verdict. NEVER throws, and never invents a lane: a missing or malformed
 * state is LOCAL TTS and inert, which is the direction the whole mechanism fails
 * in — the same fail-closed choice the loop toggle makes toward OFF.
 *
 * `name` is the agent's display name, used only inside the sentences.
 */
export function resolveTransport(
  state: AgentTransportState | null, name: string,
): TransportVerdict {
  const who = String(name || '').trim().toUpperCase() || 'THIS AGENT';

  const inert = (detail: string): TransportVerdict => ({
    label: LANE_LABEL.local_tts,
    lane: LOCAL_TTS,
    armable: false,
    next: null,
    onPhone: false,
    detail,
    hint: null,
  });

  if (!state) {
    return inert('TRANSPORT STATE UNREAD — THE LANE HAS NOT BEEN READ YET, SO IT IS '
      + 'SHOWN LOCAL BECAUSE LOCAL IS THE LANE THAT IS WIRED, NOT BECAUSE IT WAS READ LOCAL.');
  }

  // NOTHING DECLARES A PHONE LANE. Measured, not assumed: the path answers 404
  // while its sibling answers 401 on the same host.
  if (state.published === false) {
    return inert('PHONE NUMBER NOT PUBLISHED — NO ROUTE DECLARES A PHONE LANE YET, SO THIS '
      + 'CHAT CANNOT BE SWITCHED TO ONE. THE CALL BUTTON STILL PLACES A LOCAL TTS CALL '
      + 'NORMALLY, AND THIS CHIP WILL ARM ITSELF THE MOMENT THE ROUTE ANSWERS.');
  }

  // Something answered and we could not use it. NOT the same fact as above.
  if (state.published === 'unknown') {
    return inert('TRANSPORT STATE UNREAD — THE ROUTE ANSWERED WITH SOMETHING THIS SURFACE '
      + 'COULD NOT READ. SHOWN LOCAL BECAUSE LOCAL IS WHERE THIS FAILS, NOT BECAUSE IT WAS READ LOCAL.');
  }

  if (state.capability === 'unknown') {
    return inert('TRANSPORT CAPABILITY UNREAD — THE ROUTE ANSWERED BUT DID NOT SAY WHICH '
      + 'LANES ARE REACHABLE, SO NO LANE IS CLAIMED AND NOTHING MAY BE ARMED.');
  }

  if (state.capability === 'local_only') {
    return inert(`NO PHONE LANE FOR ${who} — THE ROUTE REPORTS ONLY THE LOCAL PIPELINE IS `
      + `REACHABLE FROM THIS CHAT. THE CALL BUTTON PLACES A LOCAL TTS CALL NORMALLY.`);
  }

  // ── capability === 'both'. THE CHIP IS A REAL SWITCH — AND ONLY THAT ──────
  //
  // WHAT `both` ACTUALLY MEANS, READ FROM THE BACKEND RATHER THAN ASSUMED:
  // `transport_capability` returns 'both' if and only if `outbound_configured`
  // is true, and `outbound_configured` is `bool(creds)` — CREDENTIALS EXIST.
  // That is the whole of it. It says nothing about whether a call carries
  // audio, because nothing on this route measures that.
  //
  // SO THE ARMED SENTENCE CANNOT SAY CALLS LEAVE BY THE NUMBER. It used to:
  // "CALLS FROM THIS CHAT LEAVE BY THE REXY OS NUMBER, NOT THE LOCAL PIPELINE."
  // That is an assertion about a call placing, made from a bit that only means
  // a credential is present — the same fault as painting an agent's own voice
  // on a chip while the box speaks somebody else's. The lane is SET; whether it
  // carries is not published, and unpublished is what gets said.
  //
  // IT IS STILL SELECTABLE, and that is deliberate: the setting is real, it
  // persists, and it is keyed per agent. Refusing the switch would be claiming
  // the opposite thing we do not know.
  const onPhone = state.transport === PHONE_NUMBER;
  return {
    label: onPhone ? LANE_LABEL.phone_number : LANE_LABEL.local_tts,
    lane: onPhone ? PHONE_NUMBER : LOCAL_TTS,
    armable: true,
    next: onPhone ? LOCAL_TTS : PHONE_NUMBER,
    onPhone,
    detail: onPhone
      ? `PHONE NUMBER IS SET FOR ${who} ALONE AND CHANGES NO OTHER AGENT. THIS ROUTE `
        + `REPORTS THE OUTBOUND PATH CONFIGURED; IT DOES NOT REPORT WHETHER A CALL `
        + `CARRIES AUDIO, SO THAT IS NOT CLAIMED HERE. THE ORB STILL PLACES A LOCAL `
        + `CALL — THE LANE IS CHOSEN HERE, AT THE BUTTON, NEVER ON THE AGENT.`
      : null,
    // THE SAME GAP, BEFORE THE PRESS — BUT AS A HOVER, NOT A COUNTED NOTICE.
    // Making it a notice put a permanent badge on every agent with a configured
    // lane, which is the always-on fault the notices chip exists to end. The
    // chip reading LOCAL TTS claims nothing about the phone lane, so nothing is
    // owed on the surface; someone reaching for the switch is owed the limit,
    // and a hover is where that belongs.
    hint: onPhone ? null
      : `PHONE NUMBER IS SELECTABLE — THE ROUTE REPORTS THE OUTBOUND PATH CONFIGURED. `
        + `IT DOES NOT REPORT WHETHER A CALL CARRIES AUDIO.`,
  };
}
